import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status/enum string either backend module actually returns
// (m28-education, m29-tertiary-tvet) — see each module's src/routes/*.ts and
// prisma/schema.prisma for the authoritative enum lists.
const STATUS_TONES: Record<string, string> = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GRANTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DONE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DENIED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m28-education — AdmissionApplication.state
  OFFERED: 'bg-sky-50 text-sky-700 ring-sky-200',
  WAITLISTED: 'bg-amber-50 text-amber-700 ring-amber-200',
  ACCEPTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DECLINED: 'bg-rose-50 text-rose-700 ring-rose-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',

  // m28-education — AdmissionRound.status
  RUN: 'bg-violet-50 text-violet-700 ring-violet-200',

  // m28-education — StudentRecord.status
  TRANSFERRED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  GRADUATED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m28-education — ExamRegistration.state
  REGISTERED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CONFIRMED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m28-education — ScholarshipApplication.state
  ACADEMIC_SCORED: 'bg-sky-50 text-sky-700 ring-sky-200',
  UNDER_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  SCORED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  BOND_ACCEPTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m28-education — TeacherRegister.status
  PENDING_VERIFICATION: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m28-education — Inspection.outcome (also m29 CPD audit outcome)
  PASS: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FAIL: 'bg-rose-50 text-rose-700 ring-rose-200',
  CONDITIONAL: 'bg-amber-50 text-amber-700 ring-amber-200',

  // Portal-derived display-only tones (revoked-or-not certificate/licence
  // summaries computed client-side from a nullable revokedAt/certRevokedAt
  // field, not a literal backend enum value) — used across the Exams,
  // Recognition, and Professional Register screens.
  ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  VALID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m28-education — PrivateSchoolLicence.state
  INSPECTION_SCHEDULED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  INSPECTED: 'bg-violet-50 text-violet-700 ring-violet-200',

  // m28-education — School.level / sector (informational)
  PRIMARY: 'bg-slate-100 text-slate-600 ring-slate-200',
  SECONDARY: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  COMBINED: 'bg-violet-50 text-violet-700 ring-violet-200',
  GOVERNMENT: 'bg-slate-100 text-slate-600 ring-slate-200',
  GRANT_AIDED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  PRIVATE: 'bg-violet-50 text-violet-700 ring-violet-200',

  // m29-tertiary-tvet — Profession.status
  RETIRED: 'bg-slate-100 text-slate-500 ring-slate-200',

  // m29-tertiary-tvet — ProfessionalRegistrant.status
  LAPSED: 'bg-amber-50 text-amber-700 ring-amber-200',

  // m29-tertiary-tvet — RecognitionApplication.state
  DETERMINED: 'bg-violet-50 text-violet-700 ring-violet-200',
  CERTIFIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m29-tertiary-tvet — RecognitionApplication.equivalenceOutcome
  RECOGNIZED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PARTIALLY_RECOGNIZED: 'bg-amber-50 text-amber-700 ring-amber-200',
  NOT_RECOGNIZED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m29-tertiary-tvet — RecognitionVerificationStep.outcome
  DISCREPANCY: 'bg-rose-50 text-rose-700 ring-rose-200',
  NO_RESPONSE: 'bg-slate-100 text-slate-500 ring-slate-200',

  // m29-tertiary-tvet — Apprenticeship.state
  TERMINATED: 'bg-rose-50 text-rose-700 ring-rose-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m29-tertiary-tvet — ApprenticeshipLogEntry.countersignStatus
  COUNTERSIGNED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISPUTED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // m29-tertiary-tvet — InstitutionAccreditation.state
  SITE_VISIT_SCHEDULED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  SITE_VISIT_COMPLETED: 'bg-violet-50 text-violet-700 ring-violet-200',
  ACCREDITED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // m29-tertiary-tvet — AccreditedProgramme.status is covered by
  // PENDING/ACTIVE/WITHDRAWN already mapped above.

  // m29-tertiary-tvet — InstitutionAccreditation.institutionType (informational)
  UNIVERSITY: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  TVET_COLLEGE: 'bg-violet-50 text-violet-700 ring-violet-200',
  PRIVATE_TERTIARY: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: Array<{ key: T; label: string }>; value: T; onChange: (key: T) => void }) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            value === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
